//
//  src/layouts/default/contact/wizard/Wizard.js
//
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { useMediaQuery } from "react-responsive";
import M from "materialize-css/dist/js/materialize.min.js";
import { persistGclidFromSearch, readStoredGclid } from "../../../../utils/Gclid";
import WizardSent from "./sent/Sent";
import WizardError from "./error/Error";

const MAX_FILE_SIZE = 5;
const MAX_FILES = 4;

const WIZARD_STEPS = [
    "type",
    "style",
    "placement",
    "timeline",
    "description",
    "references",
    "contact",
];

const TYPE_OPTIONS = [
    { id: "first_tattoo",   label: "My first tattoo",         hint: "Starting your story. I'll walk you through it." },
    { id: "new_piece",      label: "A new piece",             hint: "Adding to your collection." },
    { id: "continuation",   label: "Continuing existing work",hint: "Building on a sleeve or unfinished work." },
    { id: "cover_up",       label: "Cover-up",                hint: "Reworking or hiding something old." },
    { id: "touch_up",       label: "Touch-up or rework",      hint: "Refreshing a faded or older tattoo." },
    { id: "memorial",       label: "Memorial piece",          hint: "Honouring someone or something special." },
    { id: "matching",       label: "Matching or couple",      hint: "Something shared with someone close." },
    { id: "unsure",         label: "Still figuring it out",   hint: "No rush. We can work it out together." },
];

const STYLE_OPTIONS = [
    { id: "colour_realism", label: "Colour realism",          hint: "Vivid, lifelike, painted." },
    { id: "bw_realism",     label: "Black & grey realism",    hint: "Photographic depth and soft shading." },
    { id: "fine_line",      label: "Fine line",               hint: "Delicate, minimal, single-needle work." },
    { id: "neo_traditional",label: "Neo-traditional",         hint: "Bold lines, rich colour, classic feel." },
    { id: "japanese",       label: "Japanese / Irezumi",      hint: "Dragons, koi, waves, flowing scenes." },
    { id: "blackwork",      label: "Blackwork",               hint: "Bold black ink, high contrast." },
    { id: "illustrative",   label: "Illustrative",            hint: "Sketchbook feel, drawn and painterly." },
    { id: "lettering",      label: "Lettering & script",      hint: "Names, quotes, words that matter." },
    { id: "open",           label: "Open to suggestions",     hint: "Happy to talk it through and find the right fit." },
];

const PLACEMENT_OPTIONS = [
    { id: "neck",        label: "Neck" },
    { id: "shoulder",    label: "Shoulder" },
    { id: "chest",       label: "Chest" },
    { id: "back",        label: "Back" },
    { id: "upper_arm",   label: "Upper arm" },
    { id: "forearm",     label: "Forearm" },
    { id: "full_sleeve", label: "Full sleeve" },
    { id: "hand",        label: "Hand or wrist" },
    { id: "ribs",        label: "Ribs / side" },
    { id: "stomach",     label: "Stomach" },
    { id: "thigh",       label: "Thigh" },
    { id: "calf",        label: "Calf or shin" },
    { id: "foot",        label: "Foot or ankle" },
    { id: "other",       label: "Somewhere else" },
];

const TIMELINE_OPTIONS = [
    { id: "asap",       label: "As soon as possible" },
    { id: "this_month", label: "Within a month" },
    { id: "soon",       label: "Next few months" },
    { id: "exploring",  label: "Just exploring ideas" },
];

const WIZARD_HEADINGS = {
    type:        { title: "What are you thinking of?", subtitle: "Pick the one closest to what you have in mind." },
    style:       { title: "What style speaks to you?", subtitle: "My focus is realism, but anything goes for now." },
    placement:   { title: "Where on the body?",        subtitle: "A rough area is plenty. We'll refine it together." },
    timeline:    { title: "When would you like to do it?", subtitle: "No pressure. Even rough timing helps." },
    description: { title: "Describe your idea.",       subtitle: "Tell me about the subject, the mood, the story behind it, or any other details you think might help. The more you share, the better I can plan the piece with you." },
    references:  { title: "Got any references?",       subtitle: "This step is optional, but reference images help a lot. Feel free to attach photos, sketches, screenshots, or anything else that captures the look and feel you're going for." },
    contact:     { title: "How do I reach you?",       subtitle: "I'll get back to you personally." },
};

const labelFor = (options, id) => {
    if (!id) return "";
    const found = options.find(o => o.id === id);
    return found ? found.label : id;
};

const validateEmail = (email) =>
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    .test(email || "");

function StepDots({ total, current }) {
    return (
        <div className="wizard-dots" aria-hidden="true">
            {Array.from({ length: total }, (_, i) => (
                <span
                    key={i}
                    className={`wizard-dot ${i === current ? "active" : ""} ${i < current ? "done" : ""}`}
                />
            ))}
        </div>
    );
}

function ChoiceCard({ option, selected, onClick }) {
    return (
        <button
            type="button"
            className={`wizard-choice ${selected ? "selected" : ""}`}
            onClick={onClick}
        >
            <span className="wizard-choice-label">{option.label}</span>
            {option.hint && <span className="wizard-choice-hint">{option.hint}</span>}
            <span className="wizard-choice-tick" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20">
                    <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>
        </button>
    );
}

function ReferenceDropzone({ onFilesChange, currentCount }) {
    const [error, setError] = React.useState(null);
    const isMobile = useMediaQuery({ query: "(max-width: 800px)" });

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { "image/*": [] },
        maxFiles: MAX_FILES,
        maxSize: MAX_FILE_SIZE * 1024 * 1024,
        multiple: true,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) return;
            const mapped = acceptedFiles
                .slice(0, MAX_FILES - currentCount)
                .map((file) => Object.assign(file, { preview: URL.createObjectURL(file) }));
            onFilesChange((prev) => [...prev, ...mapped]);
            setError(null);
        },
        onDropRejected: (rejections) => {
            rejections.forEach(({ errors }) => {
                errors.forEach((err) => {
                    if (err.code === "file-too-large") {
                        setError(`File is too large — max size is ${MAX_FILE_SIZE} MB.`);
                    } else {
                        setError(err.message);
                    }
                });
            });
        },
    });

    return (
        <div {...getRootProps()} className="wizard-dropzone">
            <input {...getInputProps()} />
            {isDragActive ? (
                <p>Drop images here…</p>
            ) : isMobile ? (
                <p>Tap to attach reference images</p>
            ) : (
                <p>Drag &amp; drop reference images, or click to browse</p>
            )}
            <p className="wizard-dropzone-hint">Optional · up to {MAX_FILES} images · max {MAX_FILE_SIZE} MB each</p>
            {error && <p className="wizard-dropzone-error">{error}</p>}
        </div>
    );
}

export default function Wizard() {
    const navigate = useNavigate();
    const location = useLocation();

    const wizardMatch = location.pathname.match(/^\/contact\/wizard\/?([^/]*)/);
    const urlStepSlug = wizardMatch ? wizardMatch[1] : "";
    const isWizardSent = urlStepSlug === "sent";
    const isWizardError = urlStepSlug === "error";
    const isWizardTerminal = isWizardSent || isWizardError;
    const urlStepIndex = (() => {
        if (isWizardTerminal) return -1;
        if (!urlStepSlug) return 0;
        const idx = WIZARD_STEPS.indexOf(urlStepSlug);
        return idx >= 0 ? idx : 0;
    })();

    const [stepIndex, setStepIndex] = React.useState(Math.max(urlStepIndex, 0));
    const [direction, setDirection] = React.useState("forward");
    const [transitioning, setTransitioning] = React.useState(false);

    React.useEffect(() => {
        setTransitioning(false);
        if (isWizardTerminal) return;
        setStepIndex((prev) => {
            if (prev === urlStepIndex) return prev;
            setDirection(urlStepIndex >= prev ? "forward" : "backward");
            return urlStepIndex;
        });
    }, [urlStepIndex, isWizardTerminal]);

    const [answers, setAnswers] = React.useState({
        enquiry_type:      "",
        enquiry_style:     "",
        enquiry_placement: "",
        enquiry_timeline:  "",
        from_name_first:   "",
        from_name_last:    "",
        from_email:        "",
        from_phone:        "",
        contact_preference: "no_preference",
        marketing_opt_in:   false,
        description:       "",
    });
    const [files, setFiles] = React.useState([]);
    const [gclid, setGclid] = React.useState(null);
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState(null);

    React.useEffect(() => {
        persistGclidFromSearch(window.location.search);
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get("gclid");
        if (fromUrl) {
            setGclid(fromUrl);
            return;
        }
        const stored = readStoredGclid();
        if (stored) setGclid(stored);
    }, []);

    React.useEffect(() => {
        const stepKey = WIZARD_STEPS[stepIndex];
        if (!isWizardTerminal && (stepKey === "description" || stepKey === "contact")) {
            const t = setTimeout(() => M.updateTextFields(), 50);
            return () => clearTimeout(t);
        }
    }, [stepIndex, isWizardTerminal]);

    React.useEffect(() => {
        return () => files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    }, [files]);

    const goTo = (path, dir = "forward") => {
        setDirection(dir);
        setTransitioning(true);
        setTimeout(() => {
            navigate(path);
            window.scrollTo({ top: 0, behavior: "instant" });
        }, 280);
    };

    const goToStep = (index, dir) => {
        const clamped = Math.max(0, Math.min(index, WIZARD_STEPS.length - 1));
        const resolvedDir = dir || (clamped >= stepIndex ? "forward" : "backward");
        goTo(`/contact/wizard/${WIZARD_STEPS[clamped]}`, resolvedDir);
    };

    const advanceToNextStep = () => goToStep(stepIndex + 1, "forward");
    const advanceToPrevStep = () => goToStep(stepIndex - 1, "backward");

    const handleWizardBack = () => {
        if (stepIndex === 0) {
            goTo("/contact/", "backward");
        } else {
            advanceToPrevStep();
        }
    };

    const setAnswer = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

    const handleSelectAndAdvance = (key, value) => {
        setAnswer(key, value);
        setTimeout(() => advanceToNextStep(), 220);
    };

    const removeFile = (index) =>
        setFiles((prev) => prev.filter((_, i) => i !== index));

    const hasValidEmail = !!answers.from_email && validateEmail(answers.from_email);
    const hasValidPhone = !!answers.from_phone && answers.from_phone.replace(/\D/g, "").length >= 6;
    const hasDescription = !!answers.description.trim();
    const canSubmit = hasValidEmail || hasValidPhone || hasDescription;

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!canSubmit || isSubmitting) return;
        setSubmitError(null);
        setSubmitting(true);

        const fullName = `${answers.from_name_first} ${answers.from_name_last}`.trim();

        const summaryLines = [
            answers.enquiry_type      && `Type: ${labelFor(TYPE_OPTIONS, answers.enquiry_type)}`,
            answers.enquiry_style     && `Style: ${labelFor(STYLE_OPTIONS, answers.enquiry_style)}`,
            answers.enquiry_placement && `Placement: ${labelFor(PLACEMENT_OPTIONS, answers.enquiry_placement)}`,
            answers.enquiry_timeline  && `Timeline: ${labelFor(TIMELINE_OPTIONS, answers.enquiry_timeline)}`,
        ].filter(Boolean);

        const composedMessage =
            (summaryLines.length ? summaryLines.join("\n") + "\n\n" : "") +
            (answers.description.trim() || "");

        const payload = {
            from_name:          fullName,
            from_name_first:    answers.from_name_first,
            from_name_last:     answers.from_name_last,
            from_email:         answers.from_email,
            from_phone:         answers.from_phone,
            contact_preference: answers.contact_preference,
            marketing_opt_in:   answers.marketing_opt_in ? "1" : "0",
            message:            composedMessage,
            type:               "wizard",
            enquiry_type:       answers.enquiry_type,
            enquiry_style:      answers.enquiry_style,
            enquiry_placement:  answers.enquiry_placement,
            enquiry_timeline:   answers.enquiry_timeline,
        };

        const formData = new FormData();
        Object.entries(payload).forEach(([k, v]) => { if (v) formData.append(k, v); });
        if (gclid) formData.append("gclid", gclid);
        files.forEach((f) => formData.append("attachments[]", f));

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE}/guest/contact/form`, {
                method: "POST",
                body: formData,
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw err;
            }
            await response.json().catch(() => ({}));
            setSubmitting(false);
            navigate("/contact/wizard/sent/", {
                state: {
                    data: payload,
                    files: files.length,
                },
            });
            setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
        } catch (err) {
            console.error("Tattoo enquiry submission failed:", err);
            setSubmitting(false);
            setSubmitError("Something went wrong sending your enquiry. Please try again, or email jeff@jefftattooer.com directly.");
            M.toast({ html: "Network error. Please try again.", classes: "red" });
            navigate("/contact/wizard/error/", {
                state: {
                    data: payload,
                    files: files.length,
                },
            });
        }
    };

    const stepKey = WIZARD_STEPS[stepIndex];

    if (isWizardSent) {
        return (
            <div className={`contact-screen sent ${transitioning ? "leaving" : "entering"}`}>
                <WizardSent />
            </div>
        );
    }

    if (isWizardError) {
        return (
            <div className={`contact-screen error ${transitioning ? "leaving" : "entering"}`}>
                <WizardError />
            </div>
        );
    }

    return (
        <div className={`contact-screen wizard ${transitioning ? `leaving-${direction}` : `entering-${direction}`}`}>
            <div className="wizard-shell">
                <div className="wizard-heading">
                    <h1 className="wizard-heading-title">
                        {(WIZARD_HEADINGS[stepKey] && WIZARD_HEADINGS[stepKey].title) || "Enquire"}
                    </h1>
                    {WIZARD_HEADINGS[stepKey] && WIZARD_HEADINGS[stepKey].subtitle && (
                        <p className="wizard-heading-subtitle">{WIZARD_HEADINGS[stepKey].subtitle}</p>
                    )}
                </div>

                <div className="wizard-header">
                    <button
                        type="button"
                        className="wizard-back"
                        onClick={handleWizardBack}
                        aria-label="Go back"
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Back</span>
                    </button>
                    <StepDots total={WIZARD_STEPS.length} current={stepIndex} />
                    <button
                        type="button"
                        className="wizard-skip"
                        onClick={() => goTo("/contact/form/")}
                    >
                        Just send a message instead
                    </button>
                </div>

                {stepKey === "type" && (
                    <div className="wizard-step">
                        <div className="wizard-choices">
                            {TYPE_OPTIONS.map((opt) => (
                                <ChoiceCard
                                    key={opt.id}
                                    option={opt}
                                    selected={answers.enquiry_type === opt.id}
                                    onClick={() => handleSelectAndAdvance("enquiry_type", opt.id)}
                                />
                            ))}
                        </div>
                        <button type="button" className="wizard-step-skip" onClick={advanceToNextStep}>
                            Not sure? Skip this step
                        </button>
                    </div>
                )}

                {stepKey === "style" && (
                    <div className="wizard-step">
                        <div className="wizard-choices">
                            {STYLE_OPTIONS.map((opt) => (
                                <ChoiceCard
                                    key={opt.id}
                                    option={opt}
                                    selected={answers.enquiry_style === opt.id}
                                    onClick={() => handleSelectAndAdvance("enquiry_style", opt.id)}
                                />
                            ))}
                        </div>
                        <button type="button" className="wizard-step-skip" onClick={advanceToNextStep}>
                            Not sure? Skip this step
                        </button>
                    </div>
                )}

                {stepKey === "placement" && (
                    <div className="wizard-step">
                        <div className="wizard-pills wizard-pills-grid">
                            {PLACEMENT_OPTIONS.map((opt) => (
                                <button
                                    type="button"
                                    key={opt.id}
                                    className={`wizard-pill ${answers.enquiry_placement === opt.id ? "selected" : ""}`}
                                    onClick={() => handleSelectAndAdvance("enquiry_placement", opt.id)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <button type="button" className="wizard-step-skip" onClick={advanceToNextStep}>
                            Not sure? Skip this step
                        </button>
                    </div>
                )}

                {stepKey === "timeline" && (
                    <div className="wizard-step">
                        <div className="wizard-pills wizard-pills-stack">
                            {TIMELINE_OPTIONS.map((opt) => (
                                <button
                                    type="button"
                                    key={opt.id}
                                    className={`wizard-pill ${answers.enquiry_timeline === opt.id ? "selected" : ""}`}
                                    onClick={() => handleSelectAndAdvance("enquiry_timeline", opt.id)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <button type="button" className="wizard-step-skip" onClick={advanceToNextStep}>
                            Not sure? Skip this step
                        </button>
                    </div>
                )}

                {stepKey === "description" && (
                    <div className="wizard-step">
                        <div className="wizard-field wizard-field-solo">
                            <textarea
                                id="wiz_desc"
                                value={answers.description}
                                onChange={(e) => setAnswer("description", e.target.value)}
                                placeholder="The more you share, the better I can plan."
                                rows={7}
                                autoFocus
                            />
                        </div>
                        <div className="wizard-actions">
                            <button
                                type="button"
                                className="wizard-next-btn"
                                onClick={advanceToNextStep}
                            >
                                {answers.description.trim() ? "Continue" : "Skip for now"}
                                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {stepKey === "references" && (
                    <div className="wizard-step">
                        <div className="wizard-field wizard-field-solo">
                            {files.length < MAX_FILES && (
                                <ReferenceDropzone onFilesChange={setFiles} currentCount={files.length} />
                            )}
                            {files.length > 0 && (
                                <div className="wizard-thumbs">
                                    {files.map((file, i) => (
                                        <div key={i} className="wizard-thumb">
                                            <button
                                                type="button"
                                                className="wizard-thumb-remove"
                                                onClick={() => removeFile(i)}
                                                aria-label={`Remove ${file.name}`}
                                            >
                                                ×
                                            </button>
                                            <img src={file.preview} alt={file.name} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="wizard-actions">
                            <button
                                type="button"
                                className="wizard-next-btn"
                                onClick={advanceToNextStep}
                            >
                                {files.length > 0 ? "Continue" : "Skip for now"}
                                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {stepKey === "contact" && (
                    <div className="wizard-step">
                        <div className="wizard-summary">
                            {answers.enquiry_type      && <span className="wizard-chip">{labelFor(TYPE_OPTIONS,      answers.enquiry_type)}</span>}
                            {answers.enquiry_style     && <span className="wizard-chip">{labelFor(STYLE_OPTIONS,     answers.enquiry_style)}</span>}
                            {answers.enquiry_placement && <span className="wizard-chip">{labelFor(PLACEMENT_OPTIONS, answers.enquiry_placement)}</span>}
                            {answers.enquiry_timeline  && <span className="wizard-chip">{labelFor(TIMELINE_OPTIONS,  answers.enquiry_timeline)}</span>}
                        </div>

                        <form className="wizard-form" onSubmit={handleSubmit}>
                            <div className="wizard-fields">
                                <div className="wizard-field">
                                    <label htmlFor="wiz_first">First name</label>
                                    <input
                                        id="wiz_first"
                                        type="text"
                                        value={answers.from_name_first}
                                        onChange={(e) => setAnswer("from_name_first", e.target.value)}
                                        placeholder="Your first name"
                                        autoComplete="given-name"
                                    />
                                </div>
                                <div className="wizard-field">
                                    <label htmlFor="wiz_last">Last name</label>
                                    <input
                                        id="wiz_last"
                                        type="text"
                                        value={answers.from_name_last}
                                        onChange={(e) => setAnswer("from_name_last", e.target.value)}
                                        placeholder="Your last name"
                                        autoComplete="family-name"
                                    />
                                </div>
                            </div>

                            <div className="wizard-fields">
                                <div className="wizard-field">
                                    <label htmlFor="wiz_email">Email</label>
                                    <input
                                        id="wiz_email"
                                        type="email"
                                        value={answers.from_email}
                                        onChange={(e) => setAnswer("from_email", e.target.value)}
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                    />
                                </div>
                                <div className="wizard-field">
                                    <label htmlFor="wiz_phone">Phone</label>
                                    <input
                                        id="wiz_phone"
                                        type="tel"
                                        value={answers.from_phone}
                                        onChange={(e) => setAnswer("from_phone", e.target.value)}
                                        placeholder="04xx xxx xxx"
                                        autoComplete="tel"
                                    />
                                </div>
                            </div>

                            <div className="wizard-field">
                                <label htmlFor="wiz_pref">Preferred way to get in touch</label>
                                <select
                                    id="wiz_pref"
                                    className="wizard-select"
                                    value={answers.contact_preference}
                                    onChange={(e) => setAnswer("contact_preference", e.target.value)}
                                >
                                    <option value="no_preference">No preference</option>
                                    <option value="email">Email</option>
                                    <option value="phone">Phone</option>
                                    <option value="sms">SMS</option>
                                </select>
                                <p className="wizard-field-hint">Share whichever you're comfortable with and I'll use that to reach out.</p>
                            </div>

                            <label className={`wizard-optin ${answers.marketing_opt_in ? "checked" : ""}`}>
                                <input
                                    type="checkbox"
                                    checked={answers.marketing_opt_in}
                                    onChange={(e) => setAnswer("marketing_opt_in", e.target.checked)}
                                />
                                <span className="wizard-optin-box" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="14" height="14">
                                        <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <span className="wizard-optin-text">
                                    <strong>
                                        Get <span className="wizard-optin-highlight">special pricing</span> and <span className="wizard-optin-highlight">discounts</span> when they come up.
                                    </strong>
                                    <span className="wizard-optin-sub">
                                        I only reach out when I've got real value to share, whether that's a last-minute cancellation, a special rate, or a serious discount on a session. If it doesn't save you money, you won't hear from me. No spam, unsubscribe anytime.
                                    </span>
                                </span>
                            </label>

                            {submitError && (
                                <p className="wizard-error">{submitError}</p>
                            )}

                            <div className="wizard-actions">
                                <button
                                    type="submit"
                                    className="wizard-submit-btn"
                                    disabled={!canSubmit || isSubmitting}
                                >
                                    {isSubmitting ? "Sending…" : "Send Enquiry"}
                                </button>
                            </div>

                            <p className="wizard-waiver">
                                By submitting, you agree to our{" "}
                                <a href="/legal/terms-and-conditions/" title="terms and conditions">Terms and Conditions</a>{" "}
                                and{" "}
                                <a href="/legal/privacy-policy/" title="privacy policy">Privacy Policy</a>.
                            </p>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
